import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FeedRunDto, FeedStatusDto, Settings } from '@thebb/shared';
import { get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, PageTitle, Spinner, fmtTime } from '../components/ui';

/** Progress of the backfill, taking the range start from the status (cursor walks start..end). */
function backfillPercent(b: FeedStatusDto['backfill']) {
  if (b.cursor === null || b.end === null || b.start === null || b.end <= b.start) return 0;
  return Math.min(100, Math.max(0, Math.round(((b.cursor - b.start) / (b.end - b.start)) * 100)));
}

const kindLabel: Record<string, string> = { poll: 'Afstemning', refresh: 'Opfrisk åbne', shop_import: 'Shop-import', products: 'Produkter', backfill: 'Historik' };

/** 3x feed: status, run log and the x3.* settings, all on one page. */
export function FeedPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const status = useQuery({ queryKey: ['x3-status'], queryFn: () => get<FeedStatusDto>('/x3/status'), refetchInterval: 15_000 });
  const runs = useQuery({ queryKey: ['x3-runs'], queryFn: () => get<FeedRunDto[]>('/x3/runs?limit=40'), refetchInterval: 15_000 });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<Settings>('/settings'), enabled: can('settings.manage') });
  const [form, setForm] = useState<Partial<Settings>>({});
  useEffect(() => {
    if (settings.data) {
      const s = settings.data;
      setForm({ 'x3.enabled': s['x3.enabled'], 'x3.baseUrl': s['x3.baseUrl'], 'x3.token': s['x3.token'], 'x3.testMode': s['x3.testMode'], 'x3.pollMinutes': s['x3.pollMinutes'], 'x3.ordersSince': s['x3.ordersSince'], 'x3.shopActiveWithinDays': s['x3.shopActiveWithinDays'], 'x3.backfillProbeFrom': s['x3.backfillProbeFrom'], 'x3.shopToken': s['x3.shopToken'], 'x3.shopSyncHour': s['x3.shopSyncHour'] });
    }
  }, [settings.data]);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['x3-status'] });
    void qc.invalidateQueries({ queryKey: ['x3-runs'] });
    void qc.invalidateQueries({ queryKey: ['orders'] });
    void qc.invalidateQueries({ queryKey: ['shops'] });
  };
  const save = useMutation({ mutationFn: () => put('/settings', form), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['settings'] }); invalidate(); }, onError: setError });
  const poll = useMutation({ mutationFn: () => post('/x3/poll'), onSuccess: invalidate, onError: setError });
  const products = useMutation({ mutationFn: () => post('/x3/sync-products'), onSuccess: invalidate, onError: setError });
  const backfillStep = useMutation({ mutationFn: () => post('/x3/backfill/step'), onSuccess: invalidate, onError: setError });
  const backfillRestart = useMutation({ mutationFn: () => post('/x3/backfill/restart'), onSuccess: invalidate, onError: setError });

  const s = status.data;
  return (
    <div className="space-y-6">
      <PageTitle
        title="3x-feed"
        actions={
          can('shops.import') && (
            <>
              <button className="btn" disabled={products.isPending} onClick={() => products.mutate()}>Synk produkter</button>
              <button className="btn btn-primary" disabled={poll.isPending} onClick={() => poll.mutate()}>{poll.isPending ? 'Henter…' : 'Hent ordrer nu'}</button>
            </>
          )
        }
      />
      <ErrorBox error={error} />
      {s && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="card text-sm">
            <div className="label">Status</div>
            <Badge tone={s.enabled ? (s.tokenSet ? 'green' : 'amber') : 'zinc'}>{s.enabled ? (s.tokenSet ? 'aktiv' : 'mangler token') : 'slået fra'}</Badge>
            {s.testMode && <Badge tone="amber">test-ordrer</Badge>}
            <div className="mt-1 truncate text-zinc-500" title={s.baseUrl}>{s.baseUrl}</div>
          </div>
          <div className="card text-sm">
            <div className="label">Sidst hentet</div>
            <div>{fmtTime(s.lastSuccessAt)}</div>
            {s.lastRun && <div className="text-zinc-500">Seneste kørsel: {s.lastRun.ok ? 'ok' : 'fejl'} · {fmtTime(s.lastRun.startedAt)}</div>}
          </div>
          <div className="card text-sm">
            <div className="label">Data</div>
            <div>{s.orderCount} ordrer</div>
            <div>{s.shopCount} shops</div>
            <div>{s.variantCount} varianter</div>
          </div>
          <div className="card text-sm">
            <div className="label">Historik (backfill)</div>
            {!s.backfill.enabled ? (
              <div>slået fra</div>
            ) : s.backfill.done ? (
              <div><Badge tone="green">færdig</Badge> ordre {s.backfill.cursor && s.backfill.end ? `${Math.min(s.backfill.cursor, s.backfill.end)} / ${s.backfill.end}` : ''}</div>
            ) : s.backfill.cursor && s.backfill.end ? (
              <div>
                <div className="mb-1 h-2 w-full rounded bg-zinc-100">
                  <div className="h-2 rounded bg-accent" style={{ width: `${backfillPercent(s.backfill)}%` }} />
                </div>
                <div>ordrenr. {s.backfill.cursor} af {s.backfill.end}</div>
              </div>
            ) : (
              <div><Badge tone="amber">finder første ordre i år…</Badge></div>
            )}
            {can('shops.import') && (
              <div className="mt-2 flex gap-2">
                {!s.backfill.done && <button className="btn" disabled={backfillStep.isPending} onClick={() => backfillStep.mutate()}>{backfillStep.isPending ? 'Henter…' : 'Kør et trin nu'}</button>}
                <button className="btn" onClick={() => confirm('Start historikken forfra?') && backfillRestart.mutate()}>Genstart</button>
              </div>
            )}
            <div className="mt-2 text-zinc-500">Ordrer fra {settings.data?.['x3.ordersSince'] ?? '…'} · shops aktive inden for {settings.data?.['x3.shopActiveWithinDays'] ?? '…'} dage</div>
          </div>
        </div>
      )}

      {can('settings.manage') && settings.data && (
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold">Forbindelse</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="label">Base-URL (3x /api/gaveuniverset)</label>
              <input className="input" value={form['x3.baseUrl'] ?? ''} onChange={(e) => setForm({ ...form, 'x3.baseUrl': e.target.value })} />
            </div>
            <div>
              <label className="label">Token (ORDER_API_TOKEN fra 3x)</label>
              <input className="input" type="password" autoComplete="off" value={form['x3.token'] ?? ''} onChange={(e) => setForm({ ...form, 'x3.token': e.target.value })} placeholder="indtast for at ændre" />
            </div>
            <div>
              <label className="label">Shop-token (x-bb-token, Gaveuniverset-token i 3x)</label>
              <input className="input" type="password" autoComplete="off" value={form['x3.shopToken'] ?? ''} onChange={(e) => setForm({ ...form, 'x3.shopToken': e.target.value })} placeholder="indtast for at ændre" />
            </div>
            <div>
              <label className="label">Shops opdateres fra 3x kl. (time)</label>
              <input className="input" type="number" min={0} max={23} value={form['x3.shopSyncHour'] ?? 5} onChange={(e) => setForm({ ...form, 'x3.shopSyncHour': Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Ordrer fra dato</label>
              <input className="input" type="date" value={form['x3.ordersSince'] ?? ''} onChange={(e) => setForm({ ...form, 'x3.ordersSince': e.target.value })} />
            </div>
            <div>
              <label className="label">Shops aktive inden for (dage)</label>
              <input className="input" type="number" min={1} value={form['x3.shopActiveWithinDays'] ?? 180} onChange={(e) => setForm({ ...form, 'x3.shopActiveWithinDays': Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Kendt ordrenummer (startpunkt for historik)</label>
              <input className="input" type="number" min={1} value={form['x3.backfillProbeFrom'] ?? ''} onChange={(e) => setForm({ ...form, 'x3.backfillProbeFrom': Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Hent hvert (minutter)</label>
              <input className="input" type="number" min={1} max={60} value={form['x3.pollMinutes'] ?? 2} onChange={(e) => setForm({ ...form, 'x3.pollMinutes': Number(e.target.value) })} />
            </div>
            <div className="flex items-end gap-4 pb-1 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!form['x3.enabled']} onChange={(e) => setForm({ ...form, 'x3.enabled': e.target.checked })} /> Feed slået til</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!form['x3.testMode']} onChange={(e) => setForm({ ...form, 'x3.testMode': e.target.checked })} /> Brug 3x test-ordrer</label>
            </div>
          </div>
          <div className="flex justify-end">
            <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Gem</button>
          </div>
        </div>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Kørsler</h2>
        {runs.isLoading ? (
          <Spinner />
        ) : (
          <table className="table card p-0">
            <thead>
              <tr>
                <th>Start</th>
                <th>Type</th>
                <th>Resultat</th>
                <th className="text-right">Hentet</th>
                <th className="text-right">Nye</th>
                <th className="text-right">Opdateret</th>
                <th className="text-right">Sprunget over</th>
                <th className="text-right">Fejl</th>
                <th>Besked</th>
              </tr>
            </thead>
            <tbody>
              {runs.data?.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap">{fmtTime(r.startedAt)}</td>
                  <td>{kindLabel[r.kind] ?? r.kind}{r.kind === 'shop_import' && ` ${(r.params as { externalShopId?: number } | null)?.externalShopId ?? ''}`}</td>
                  <td>{r.ok === null ? <Badge tone="amber">kører</Badge> : r.ok ? <Badge tone="green">ok</Badge> : <Badge tone="red">fejl</Badge>}</td>
                  <td className="text-right tabular-nums">{r.fetched}</td>
                  <td className="text-right tabular-nums">{r.created}</td>
                  <td className="text-right tabular-nums">{r.updated}</td>
                  <td className="text-right tabular-nums">{r.skipped}</td>
                  <td className="text-right tabular-nums">{r.errors}</td>
                  <td className="max-w-md truncate text-xs text-zinc-600" title={r.message ?? ''}>{r.message?.split('\n')[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
